import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import type { RealtimeNotice } from '@deskworks/domain';
import { createContext, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import type { ApiClient } from '../api.js';
import { ConversationStore, type TimelineState, type TimelineTransport } from '../conversation-store.js';
import type { RealtimeClient, RealtimeStatus } from '../realtime.js';

interface ClientContextValue {
  api: ApiClient;
  realtime: RealtimeClient | null;
}

const ClientContext = createContext<ClientContextValue | null>(null);

/** Shared data layer: in-memory query cache plus realtime-driven invalidation. */
export function ClientProvider({
  api,
  realtime,
  queryClient,
  children,
}: {
  api: ApiClient;
  realtime: RealtimeClient | null;
  queryClient: QueryClient;
  children: ReactNode;
}) {
  const value = useMemo(() => ({ api, realtime }), [api, realtime]);
  return (
    <QueryClientProvider client={queryClient}>
      <ClientContext.Provider value={value}>
        {realtime ? <RealtimeInvalidation realtime={realtime} /> : null}
        {children}
      </ClientContext.Provider>
    </QueryClientProvider>
  );
}

export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (count, err) => {
          const status = (err as { status?: number }).status ?? 0;
          return status === 0 ? count < 3 : status >= 500 && count < 2;
        },
        refetchOnWindowFocus: true,
      },
    },
  });
}

export function useApi(): ApiClient {
  const ctx = useContext(ClientContext);
  if (!ctx) throw new Error('ClientProvider missing');
  return ctx.api;
}

export function useRealtime(): RealtimeClient | null {
  return useContext(ClientContext)?.realtime ?? null;
}

export function useRealtimeEvent<T extends RealtimeNotice['type']>(
  type: T,
  handler: (notice: Extract<RealtimeNotice, { type: T }>) => void,
) {
  const realtime = useRealtime();
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!realtime) return;
    return realtime.on(type, (n) => ref.current(n as Extract<RealtimeNotice, { type: T }>));
  }, [realtime, type]);
}

export function useRealtimeStatus(): RealtimeStatus {
  const realtime = useRealtime();
  const [status, setStatus] = useState<RealtimeStatus>(realtime?.status ?? 'idle');
  useEffect(() => {
    if (!realtime) return;
    setStatus(realtime.status);
    return realtime.onStatus(setStatus);
  }, [realtime]);
  return status;
}

export const qk = {
  session: ['session'] as const,
  conversations: ['conversations'] as const,
  accounts: ['accounts'] as const,
  mailFolders: ['mail', 'folders'] as const,
  mail: ['mail'] as const,
  calendar: ['calendar'] as const,
  operations: ['operations'] as const,
  activity: ['activity'] as const,
  meetings: ['meetings'] as const,
  occurrence: (id: string) => ['occurrence', id] as const,
  people: ['people'] as const,
  files: ['files'] as const,
  remoteHosts: ['remote', 'hosts'] as const,
  remoteSessions: ['remote', 'sessions'] as const,
  remoteSession: (id: string) => ['remote', 'session', id] as const,
  remoteKeys: ['remote', 'keys'] as const,
};

function RealtimeInvalidation({ realtime }: { realtime: RealtimeClient }) {
  const qc = useQueryClient();
  useEffect(() => {
    const offs = [
      realtime.on('conversation.list', () => qc.invalidateQueries({ queryKey: qk.conversations })),
      realtime.on('conversation.membership', () => qc.invalidateQueries({ queryKey: qk.conversations })),
      realtime.on('conversation.changed', () => qc.invalidateQueries({ queryKey: qk.conversations })),
      realtime.on('read.changed', () => qc.invalidateQueries({ queryKey: qk.conversations })),
      realtime.on('mail.changed', () => qc.invalidateQueries({ queryKey: qk.mail })),
      realtime.on('calendar.changed', () => qc.invalidateQueries({ queryKey: qk.calendar })),
      realtime.on('account.status', () => {
        void qc.invalidateQueries({ queryKey: qk.accounts });
        void qc.invalidateQueries({ queryKey: qk.mail });
        void qc.invalidateQueries({ queryKey: qk.calendar });
      }),
      realtime.on('operation.changed', () => {
        void qc.invalidateQueries({ queryKey: qk.operations });
        void qc.invalidateQueries({ queryKey: qk.mail });
        void qc.invalidateQueries({ queryKey: qk.calendar });
      }),
      realtime.on('activity.new', () => qc.invalidateQueries({ queryKey: qk.activity })),
      realtime.on('remote.hosts', () => qc.invalidateQueries({ queryKey: qk.remoteHosts })),
      realtime.on('remote.keys', () => qc.invalidateQueries({ queryKey: qk.remoteKeys })),
      realtime.on('remote.session', (n) => {
        if (n.type === 'remote.session') void qc.invalidateQueries({ queryKey: qk.remoteSession(n.sessionId) });
        void qc.invalidateQueries({ queryKey: qk.remoteSessions });
      }),
      realtime.on('meeting.changed', (n) => {
        if (n.type === 'meeting.changed') void qc.invalidateQueries({ queryKey: qk.occurrence(n.occurrenceId) });
        void qc.invalidateQueries({ queryKey: qk.meetings });
      }),
      realtime.on('meeting.lobby', (n) => {
        if (n.type === 'meeting.lobby') void qc.invalidateQueries({ queryKey: qk.occurrence(n.occurrenceId) });
      }),
      // After a reconnect everything may be stale.
      realtime.onReconnected(() => void qc.invalidateQueries()),
    ];
    return () => offs.forEach((off) => off());
  }, [realtime, qc]);
  return null;
}

/**
 * Live conversation timeline with backfill on realtime notices and reconnects, and a visible
 * outbox for unsent messages.
 */
export function useConversationTimeline(conversationId: string | null, transport?: TimelineTransport) {
  const api = useApi();
  const realtime = useRealtime();
  const store = useMemo(() => {
    if (!conversationId) return null;
    const t: TimelineTransport = transport ?? {
      list: (o) => api.messages(conversationId, o),
      changes: (since) => api.changesSince(conversationId, since),
      post: (i) => api.postMessage(conversationId, i),
    };
    return new ConversationStore(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, api]);

  useEffect(() => {
    if (!store) return;
    store.resume();
    void store.load();
    return () => store.dispose();
  }, [store]);

  useEffect(() => {
    if (!store || !realtime || !conversationId) return;
    const offs = [
      realtime.on('conversation.changed', (n) => {
        if (n.type === 'conversation.changed' && n.conversationId === conversationId) void store.backfill(n.changeSeq);
      }),
      realtime.on('conversation.membership', (n) => {
        if (n.type === 'conversation.membership' && n.conversationId === conversationId && n.removed) void store.backfill();
      }),
      realtime.onReconnected(() => {
        void store.backfill();
        store.retryFailed();
      }),
    ];
    return () => offs.forEach((off) => off());
  }, [store, realtime, conversationId]);

  const empty: TimelineState = EMPTY;
  const state = useSyncExternalStore(store?.subscribe ?? noopSubscribe, store?.getSnapshot ?? (() => empty), store?.getSnapshot ?? (() => empty));
  return { state, store };
}

const EMPTY: TimelineState = {
  status: 'loading',
  error: null,
  messages: [],
  threads: {},
  outbox: [],
  changeSeq: 0,
  hasMoreBefore: false,
  loadingOlder: false,
};
const noopSubscribe = () => () => undefined;
